import { Component, OnInit, signal, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

import { InboundService } from '../inbound.service';
import { WarehouseResultInfoVO } from '@app/api/model/warehouseResultInfoVO';
import { WarehouseWarrantDetailItem } from '../models/warehouse-warrant-detail-item';
import { InboundFormWrapperComponent } from '../inbound-form-wrapper/inbound-form-wrapper.component';

@Component({
  selector: 'app-inbound-result-view',
  standalone: true,
  imports: [CommonModule, TranslateModule, InboundFormWrapperComponent],
  providers: [MessageService],
  templateUrl: './inbound-result-view.component.html',
  styleUrl: './inbound-result-view.component.css',
})
export class InboundResultViewComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly inboundService = inject(InboundService);
  private readonly translateService = inject(TranslateService);
  private readonly destroy$ = new Subject<void>();

  // Data
  warehouseResultData = signal<WarehouseResultInfoVO | null>(null);
  itemDetails = signal<WarehouseWarrantDetailItem[]>([]);
  loading = signal(false);

  // Convert result data to warrant data format for the form wrapper
  get inboundData() {
    const result = this.warehouseResultData();
    if (!result) return null;

    // Calculate total planned quantity from details array
    const totalPlannedQty = this.calculateTotalPlannedQty(result);

    // Transform WarehouseResultInfoVO to WarehouseWarrantInfoVO format
    return {
      id: result.id,
      code: result.code,
      warehouseWarrantCode:
        (result as any).warehouseWarrantCode ||
        (result as any).warehousewarrantCode,
      platformOrderCode: result.platformOrderCode,
      orderCode: result.orderCode,
      inputType: result.inputType,
      inputTypeMeaning: result.inputTypeMeaning,
      status: result.status,
      statusMeaning: result.statusMeaning,
      receivingLogicWarehouseName: result.receivingLogicWarehouseName,
      receivingLogicWarehouseCode: result.receivingLogicWarehouseCode,
      storeName: result.storeName,
      channelTypeCode: result.channelTypeCode,
      channelTypeCodeMeaning: result.channelTypeCodeMeaning,
      creationDate: result.creationDate,
      alreadyTotalQty: result.alreadyQty,
      totalQty: totalPlannedQty,
      statusPossibleNext: [],
      // Add return information for return orders
      returnsName: (result as any).returnsName,
      returnsTel: (result as any).returnsTel,
      returnsAddress: (result as any).returnsAddress,
      // Add logistics information
      logisticsList: (result as any).logisticsList,
    };
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');

    if (id) {
      this.loadWarehouseResultData(Number(id));
    } else {
      this.router.navigate(['/stock/inbound']);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFormAction(event: { action: string; data?: any }): void {
    if (event.action === 'cancel') {
      this.router.navigate(['/stock/inbound'], {
        queryParams: { tab: 'inbound-result' },
      });
    } else if (event.action === 'go_back') {
      this.router.navigate(['/stock/inbound'], {
        queryParams: { tab: 'inbound-result' },
      });
    }
  }

  onTableAction(event: { section: string; action: string; data?: any }): void {
    // Handle table actions if needed
    console.log('Table action:', event);
  }

  /**
   * Calculate total planned quantity from details array
   */
  private calculateTotalPlannedQty(result: WarehouseResultInfoVO): number {
    const details = (result as any).details;
    if (!details || !Array.isArray(details)) {
      return 0;
    }

    return details.reduce((total, detail: any) => {
      const plannedQty = detail.plannedQty || 0;
      return total + plannedQty;
    }, 0);
  }

  private loadWarehouseResultData(id: number): void {
    this.loading.set(true);

    this.inboundService
      .getWarehouseResultDetail(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.warehouseResultData.set(response);

          // Load actual item details from the response
          if (response.details && Array.isArray(response.details)) {
            const transformedDetails = response.details.map((detail: any) => ({
              id: detail.id,
              skuCode: detail.skuCode,
              skuName: detail.skuName,
              spuCode: detail.spuCode,
              spuName: detail.spuName,
              plannedQty: detail.plannedQty || 0, // Use plannedQty if available, otherwise 0
              receivedQty: detail.alreadyQty || 0, // Use alreadyQty as received quantity
              alreadyQty: detail.alreadyQty || 0, // Keep original field for compatibility
              lotValue: detail.lotValue,
              serialNumbers: detail.serialNumbers,
            }));
            this.itemDetails.set(transformedDetails);
          } else {
            this.itemDetails.set([]);
          }

          this.loading.set(false);
        },
        error: (error) => {
          this.loading.set(false);
          console.error('Failed to load warehouse result details:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load warehouse result details',
          });
        },
      });
  }
}
